import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/navigation/Header';

const SUPPORT_EMAIL = 'support@notifications.angle.dating';
const APP_NAME = 'Angle!';
const COMPANY_NAME = 'Angle App';
const EFFECTIVE_DATE = '04 April 2026';
const APP_URL = 'https://angle.dating/';

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">{title}</h2>
    <div className="space-y-3 text-gray-700 leading-relaxed">{children}</div>
  </section>
);

const TermsOfService = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Terms of Service | Angle! Photo Ranking App</title>
        <meta name="description" content="Read the Terms of Service for Angle! – the AI-powered photo ranking app. Understand your rights and responsibilities when using our service." />
        <link rel="canonical" href="https://angle.dating/terms-of-service" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://angle.dating/terms-of-service" />
        <meta property="og:title" content="Terms of Service | Angle!" />
        <meta property="og:description" content="Read the Terms of Service for Angle! – the AI-powered photo ranking app." />
        <meta property="og:site_name" content="Angle!" />
      </Helmet>
      <Header
        userProp={null}
        onNavigate={() => {}}
        onPurchaseCredits={() => {}}
        onLogout={() => {}}
      />
      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Title block */}
        <div className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Terms of Service</h1>
          <p className="text-gray-500 text-sm">
            Effective Date: <strong>{EFFECTIVE_DATE}</strong> &nbsp;·&nbsp; Last Updated: <strong>{EFFECTIVE_DATE}</strong>
          </p>
          <div className="mt-4 p-4 bg-violet-50 border border-violet-200 rounded-lg text-sm text-violet-800">
            Please read these Terms of Service carefully before using {APP_NAME}. By accessing or using our service, you agree to be bound by these terms.
          </div>
        </div>

        <Section title="1. Acceptance of Terms">
          <p>
            These Terms of Service ("Terms") constitute a legally binding agreement between you ("User," "you," or "your") and {COMPANY_NAME} ("Company," "we," "us," or "our") governing your access to and use of the {APP_NAME} application and related services (collectively, the "Service") available at <a href={APP_URL} className="text-violet-600 underline">{APP_URL}</a>.
          </p>
          <p>
            By creating an account, purchasing credits, or otherwise using the Service, you confirm that you are at least 18 years of age, have read and understood these Terms, and agree to be bound by them. If you do not agree, you must not use the Service.
          </p>
        </Section>

        <Section title="2. Description of Service">
          <p>
            {APP_NAME} is an AI-powered photo ranking and analysis platform. Users upload photographs, and our algorithms analyze and rank them based on composition, lighting, facial expression, and overall visual appeal. The Service is intended to help users select their best photos for personal use, social media, dating profiles, and similar purposes.
          </p>
          <p>
            The Service operates on a credit-based model. Each photo analysis session consumes credits purchased by the user. Credits are non-refundable except as expressly stated in Section 7 (Refunds and Cancellations).
          </p>
        </Section>

        <Section title="3. User Accounts">
          <p>
            <strong>Registration:</strong> You must create an account to access most features of the Service. You agree to provide accurate, current, and complete information during registration and to keep your account information updated.
          </p>
          <p>
            <strong>Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must notify us immediately at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-violet-600 underline">{SUPPORT_EMAIL}</a> if you suspect unauthorized access.
          </p>
          <p>
            <strong>One Account Per User:</strong> You may not create multiple accounts to circumvent credit limits, promotional offers, or any restrictions imposed by these Terms.
          </p>
          <p>
            <strong>Age Requirement:</strong> You must be at least 18 years old to use the Service. We do not knowingly collect information from minors.
          </p>
        </Section>

        <Section title="4. Acceptable Use">
          <p>You agree to use the Service only for lawful purposes and in accordance with these Terms. You must not:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Upload photographs of individuals without their explicit consent.</li>
            <li>Upload photographs depicting nudity, sexual content, violence, or any content that is illegal, harmful, or offensive.</li>
            <li>Upload photographs of minors in any context.</li>
            <li>Attempt to reverse-engineer, decompile, or extract the underlying AI models or algorithms.</li>
            <li>Use automated scripts, bots, or other means to access the Service in a manner that exceeds normal human usage.</li>
            <li>Resell, sublicense, or commercially exploit the Service or its outputs without our prior written consent.</li>
            <li>Interfere with or disrupt the integrity or performance of the Service or its infrastructure.</li>
            <li>Attempt to gain unauthorized access to any part of the Service or its related systems.</li>
          </ul>
          <p>
            We reserve the right to suspend or terminate accounts that violate these provisions without notice or refund.
          </p>
        </Section>

        <Section title="5. Intellectual Property">
          <p>
            <strong>Our Content:</strong> The Service, including its design, software, algorithms, text, graphics, and all other content created by us, is owned by {COMPANY_NAME} and protected by applicable intellectual property laws. You may not copy, modify, distribute, or create derivative works without our express written permission.
          </p>
          <p>
            <strong>Your Content:</strong> You retain ownership of all photographs and content you upload to the Service ("User Content"). By uploading User Content, you grant us a limited, non-exclusive, royalty-free license to process, analyze, and display your User Content solely for the purpose of providing the Service to you. We do not use your photographs to train our AI models without your explicit consent.
          </p>
          <p>
            <strong>AI-Generated Outputs:</strong> Rankings, scores, and insights generated by the Service are provided for informational purposes only. You may use these outputs for personal, non-commercial purposes.
          </p>
        </Section>

        <Section title="6. Credits and Payments">
          <p>
            <strong>Credit Packages:</strong> The Service offers the following credit packages:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Starter Pack — 10 credits for $9.99</li>
            <li>Popular Pack — 25 credits for $19.99</li>
            <li>Pro Pack — 50 credits for $34.99</li>
          </ul>
          <p>
            <strong>Payment Processing:</strong> All payments are processed securely by Stripe, Inc. By purchasing credits, you authorize us to charge your payment method for the selected package. We do not store your full payment card details.
          </p>
          <p>
            <strong>Credit Expiry:</strong> Credits are valid for the period specified at the time of purchase (typically 6–12 months). Unused credits expire at the end of the validity period and are not refundable.
          </p>
          <p>
            <strong>Taxes:</strong> Prices displayed may not include applicable taxes. Any applicable sales tax, VAT, or similar charges will be added at checkout based on your jurisdiction.
          </p>
        </Section>

        <Section title="7. Refunds and Cancellations">
          <p>
            <strong>General Policy:</strong> Due to the digital nature of the Service, all credit purchases are generally non-refundable once credits have been used.
          </p>
          <p>
            <strong>Unused Credits:</strong> If you have not used any credits from a recently purchased package (within 7 days of purchase), you may request a refund by contacting us at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-violet-600 underline">{SUPPORT_EMAIL}</a>. Refund requests are evaluated on a case-by-case basis.
          </p>
          <p>
            <strong>Technical Failures:</strong> If credits are deducted due to a verified technical error on our part, we will restore the affected credits to your account or issue a refund at our discretion.
          </p>
          <p>
            <strong>Chargebacks:</strong> Initiating a chargeback without first contacting our support team may result in immediate account suspension. We encourage you to resolve disputes directly with us.
          </p>
        </Section>

        <Section title="8. Privacy">
          <p>
            Your use of the Service is also governed by our <Link to="/privacy-policy" className="text-violet-600 underline">Privacy Policy</Link>, which is incorporated into these Terms by reference. Please review our Privacy Policy to understand how we collect, use, and protect your personal information.
          </p>
        </Section>

        <Section title="9. Disclaimers and Limitation of Liability">
          <p>
            <strong>No Guarantee of Results:</strong> The AI rankings and scores provided by the Service are algorithmic assessments and do not constitute professional advice. Results may vary and are not guaranteed to improve your social media performance, dating success, or any other outcome.
          </p>
          <p>
            <strong>"As Is" Service:</strong> THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
          </p>
          <p>
            <strong>Limitation of Liability:</strong> TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, {COMPANY_NAME?.toUpperCase()} SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL, ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY TO YOU FOR ANY CLAIM SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE 12 MONTHS PRECEDING THE CLAIM.
          </p>
        </Section>

        <Section title="10. Indemnification">
          <p>
            You agree to indemnify, defend, and hold harmless {COMPANY_NAME} and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of or in any way connected with: (a) your access to or use of the Service; (b) your User Content; (c) your violation of these Terms; or (d) your violation of any third-party rights.
          </p>
        </Section>

        <Section title="11. Termination">
          <p>
            <strong>By You:</strong> You may terminate your account at any time by contacting us at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-violet-600 underline">{SUPPORT_EMAIL}</a>. Upon termination, your unused credits will be forfeited unless a refund is approved under Section 7.
          </p>
          <p>
            <strong>By Us:</strong> We reserve the right to suspend or terminate your account and access to the Service at any time, with or without cause, and with or without notice. Grounds for termination include, but are not limited to, violation of these Terms, fraudulent activity, or extended inactivity.
          </p>
          <p>
            <strong>Effect of Termination:</strong> Upon termination, all licenses granted to you under these Terms will immediately cease. Sections 5, 9, 10, and 13 shall survive termination.
          </p>
        </Section>

        <Section title="12. Changes to Terms">
          <p>
            We reserve the right to modify these Terms at any time. When we make material changes, we will update the "Last Updated" date at the top of this page and, where appropriate, notify you by email or in-app notification. Your continued use of the Service after such changes constitutes your acceptance of the revised Terms.
          </p>
        </Section>

        <Section title="13. Governing Law and Dispute Resolution">
          <p>
            These Terms shall be governed by and construed in accordance with applicable law. Any disputes arising under or in connection with these Terms shall first be attempted to be resolved through good-faith negotiation. If negotiation fails, disputes shall be resolved through binding arbitration, except that either party may seek injunctive or other equitable relief in a court of competent jurisdiction.
          </p>
        </Section>

        <Section title="14. Contact Us">
          <p>
            If you have any questions about these Terms of Service, please contact us:
          </p>
          <div className="mt-3 p-4 bg-gray-100 rounded-lg">
            <p className="font-semibold text-gray-900">{COMPANY_NAME}</p>
            <p className="text-gray-700">
              Email: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-violet-600 underline">{SUPPORT_EMAIL}</a>
            </p>
          </div>
        </Section>
      </main>
      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 px-4 mt-12">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-gray-500">© {new Date()?.getFullYear()} {COMPANY_NAME}. All rights reserved.</p>
          <div className="flex items-center gap-4 text-xs">
            <Link to="/terms-of-service" className="hover:text-gray-200 transition-colors font-medium text-gray-300">Terms of Service</Link>
            <Link to="/privacy-policy" className="hover:text-gray-200 transition-colors">Privacy Policy</Link>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-gray-200 transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
